import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { Link } from 'react-router-dom';
import { 
  ChevronRight,
  FileText,
  ArrowRight,
  BookOpen,
  Award,
  Layers,
  Target,
  Zap,
  FileCheck,
  FolderOpen,
  Users,
  Archive,
  Eye,
  Download,
  Calendar,
  Search,
  Database,
  Library,
  BarChart3,
  GraduationCap,
  Clock,
  X,
  AlertCircle,
  Mail,
  Menu
} from 'lucide-react';
import PublicSubmit from '../components/PublicSubmit';
import ContactModal from '../components/ContactModal';

function HomePage() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [programs, setPrograms] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [resources, setResources] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredResources, setFilteredResources] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get resource count for a degree
  const getResourceCountForDegree = (degreeId) => {
    return resources.filter(r => r.degreeId === degreeId).length;
  };

  // Get levels for a degree
  const getLevelsForDegree = (degreeId) => {
    return levels.filter(l => l.degree?.id === degreeId);
  };

  // Fetch all data from Strapi
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [programsRes, degreesRes, subjectsRes, resourcesRes, levelsRes] = await Promise.all([
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/programs'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees?populate=program'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects?populate=level'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources?populate[subject][populate][level][populate][degree][populate]=program'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels?populate=degree')
        ]);
        
        const programsData = await programsRes.json();
        const degreesData = await degreesRes.json();
        const subjectsData = await subjectsRes.json();
        const resourcesData = await resourcesRes.json();
        const levelsData = await levelsRes.json();
        
        const allResources = resourcesData.data || [];
        const approvedResources = allResources.filter(r => r.approvalStatus === 'approved');
        
        // Transform resources with documentId
        const transformedResources = approvedResources.map(r => {
          const subject = r.subject;
          const level = subject?.level;
          const degree = level?.degree;
          const program = degree?.program;
          
          return {
            id: r.id,
            documentId: r.documentId,
            title: r.title,
            type: r.type,
            createdAt: r.createdAt,
            file: r.file,
            subjectId: subject?.id,
            subjectName: subject?.name,
            subjectSemester: subject?.semester,
            levelId: level?.id,
            levelName: level?.name,
            degreeId: degree?.id,
            degreeName: degree?.name,
            programId: program?.id,
            programName: program?.name
          };
        });
        
        setPrograms(programsData.data || []);
        setDegrees(degreesData.data || []);
        setSubjects(subjectsData.data || []);
        setResources(transformedResources);
        setLevels(levelsData.data || []);
        setFilteredResources(transformedResources);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter resources by type
  useEffect(() => {
    let filtered = [...resources];
    if (activeTypeFilter !== 'all') {
      filtered = filtered.filter(r => r.type?.toUpperCase() === activeTypeFilter.toUpperCase());
    }
    setFilteredResources(filtered);
  }, [activeTypeFilter, resources]);

  // Get ALL departments from ALL degrees
  const departments = degrees.map(dept => ({
    id: dept.id,
    name: dept.name,
    fullName: getFullName(dept.name),
    color: getColor(dept.name),
    icon: getIcon(dept.name),
    count: getResourceCountForDegree(dept.id),
    levels: getLevelsForDegree(dept.id)
  }));

  // Programs list
  const programList = programs.map(p => ({
    id: p.id,
    name: p.name,
    icon: getProgramIcon(p.name),
    description: getProgramDesc(p.name),
    color: getProgramColor(p.name),
    count: resources.filter(r => r.programId === p.id).length,
    hasData: resources.filter(r => r.programId === p.id).length > 0
  }));

  // Recent resources - ONLY 3 items
  const recentResources = [...filteredResources]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3)
    .map(r => ({
      id: r.id,
      documentId: r.documentId,
      title: r.title,
      type: r.type || 'DOCUMENT',
      subject: r.subjectName || 'Sans matière',
      level: r.levelName || 'N/A',
      department: r.degreeName || 'ISIMM',
      date: r.createdAt ? new Date(r.createdAt).toISOString().split('T')[0] : '2026'
    }));

  function getFullName(name) {
    const names = { 
      'INFO': 'Informatique', 
      'MATH': 'Mathématiques', 
      'TIC': 'TIC', 
      'EEA': 'Électronique',
      'CPI1': 'Cycle Préparatoire 1ère année',
      'CPI2': 'Cycle Préparatoire 2ème année',
      'Cycle preparatoire 1': 'Cycle Préparatoire 1ère année',
      'Cycle preparatoire 2': 'Cycle Préparatoire 2ème année'
    };
    return names[name] || name;
  }

  function getColor(name) {
    const colors = { 
      'INFO': 'mint', 
      'MATH': 'pink', 
      'TIC': 'yellow', 
      'EEA': 'orange',
      'CPI1': 'mint',
      'CPI2': 'mint',
      'Cycle preparatoire 1': 'mint',
      'Cycle preparatoire 2': 'mint'
    };
    return colors[name] || 'mint';
  }

  function getIcon(name) {
    const icons = {
      'INFO': <GraduationCap size={28} />,
      'MATH': <BookOpen size={28} />,
      'TIC': <Zap size={28} />,
      'EEA': <Target size={28} />,
      'CPI1': <Target size={28} />,
      'CPI2': <Target size={28} />,
      'Cycle preparatoire 1': <Target size={28} />,
      'Cycle preparatoire 2': <Target size={28} />
    };
    return icons[name] || <GraduationCap size={28} />;
  }

  function getProgramIcon(name) {
    const icons = {
      'Licence': <BookOpen size={28} />,
      'Mastere': <Award size={28} />,
      'Cycle preparatoire': <Target size={28} />,
      'Cycle Ingenieur': <Layers size={28} />
    };
    return icons[name] || <GraduationCap size={28} />;
  }

  function getProgramColor(name) {
    const colors = {
      'Licence': 'pink',
      'Mastere': 'orange',
      'Cycle preparatoire': 'mint',
      'Cycle Ingenieur': 'mint'
    };
    return colors[name] || 'mint';
  }

  function getProgramDesc(name) {
    const desc = {
      'Licence': 'Formation fondamentale universitaire',
      'Mastere': 'Spécialisation avancée',
      'Cycle preparatoire': 'Préparez votre future spécialité',
      'Cycle Ingenieur': "Formation d'excellence"
    };
    return desc[name] || 'Formation académique';
  }

  // Smart search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const query = searchQuery.toLowerCase().trim();
    const results = [];
    
    resources.forEach(resource => {
      const title = resource.title?.toLowerCase() || '';
      if (title.includes(query)) {
        results.push({
          id: resource.documentId,
          title: resource.title,
          subtitle: resource.type,
          type: 'resource',
          icon: '📄',
          link: `/resource/${resource.documentId}`
        });
      }
    });
    
    degrees.forEach(degree => {
      const name = degree.name?.toLowerCase() || '';
      if (name.includes(query)) {
        results.push({
          id: degree.id,
          title: degree.name,
          subtitle: 'Département',
          type: 'department',
          icon: '🏛️',
          link: `/department/${degree.name}`
        });
      }
    });
    
    subjects.forEach(subject => {
      const name = subject.name?.toLowerCase() || '';
      if (name.includes(query)) {
        results.push({
          id: subject.id,
          title: subject.name,
          subtitle: 'Matière',
          type: 'subject',
          icon: '📚',
          link: `/browse?subject=${subject.id}`
        });
      }
    });
    
    setSearchResults(results.slice(0, 8));
    setShowSearchResults(true);
  }, [searchQuery, resources, degrees, subjects]);

  const resourceTypes = [
    { id: 'all', name: 'Tous', icon: <Layers size={12} />, count: resources.length },
    { id: 'cours', name: 'Cours', icon: <BookOpen size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'COURS').length },
    { id: 'examens', name: 'Examens', icon: <FileText size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'EXAMENS').length },
    { id: 'tp', name: 'TP', icon: <Zap size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'TP').length },
    { id: 'td', name: 'TD', icon: <FileCheck size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'TD').length },
    { id: 'projets', name: 'Projets', icon: <FolderOpen size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'PROJETS').length },
  ];

  const totalResources = resources.length;
  const totalSubjects = subjects.length;
  const totalDepartments = departments.length;

  const stats = [
    { id: 1, value: totalResources || '0', label: 'Documents', icon: <Database size={22} />, color: 'mint' },
    { id: 2, value: totalSubjects || '0', label: 'Matières', icon: <Library size={22} />, color: 'pink' },
    { id: 3, value: '2k+', label: 'Étudiants', icon: <Users size={22} />, color: 'orange' },
    { id: 4, value: totalDepartments || '0', label: 'Départements', icon: <BarChart3 size={22} />, color: 'mint' },
  ];

  const getTypeIcon = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return <BookOpen size={16} className="text-mint" />;
      case 'EXAMENS': return <FileText size={16} className="text-pink" />;
      case 'TP': return <Zap size={16} className="text-yellow" />;
      case 'TD': return <FileCheck size={16} className="text-orange" />;
      case 'PROJETS': return <FolderOpen size={16} className="text-mint" />;
      default: return <FileText size={16} className="text-white/40" />;
    }
  };

  const getTypeColor = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return 'border-mint/30 text-mint';
      case 'EXAMENS': return 'border-pink/30 text-pink';
      case 'TP': return 'border-yellow/30 text-yellow';
      case 'TD': return 'border-orange/30 text-orange';
      case 'PROJETS': return 'border-mint/30 text-mint';
      default: return 'border-white/20 text-white/40';
    }
  };

  return (
    <div className="bg-dark-bg min-h-screen">
      
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-80 h-80 bg-mint/5 rounded-full blur-[120px] animate-float"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-pink/5 rounded-full blur-[120px] animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'glass-dark border-b border-dark-border' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3 sm:gap-4">
  <img 
    src={logo} 
    alt="ISIMM Archive" 
    className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
    style={{ scale: '1.3' }}
  />
  <div>
    <span className="text-lg sm:text-xl font-mono tracking-tighter text-white">ISIMM<span className="gradient-text">_Archive</span></span>
    <p className="font-mono text-[7px] sm:text-[9px] tracking-wider text-white/40 hidden sm:block">digital library</p>
  </div>
</Link>
            
            {/* Desktop Search Bar */}
            <div className="hidden md:block relative w-96">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Rechercher un cours, examen, département, matière..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                  className="font-body text-sm w-full pl-5 pr-12 py-3 glass-dark rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30" size={18} />
              </div>
              
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 glass-dark rounded-xl border border-white/10 overflow-hidden z-50">
                  <div className="p-2 border-b border-white/10 flex justify-between items-center">
                    <span className="font-mono text-[9px] text-white/30 px-2">RÉSULTATS POUR "{searchQuery}"</span>
                    <button onClick={() => setShowSearchResults(false)} className="text-white/30 hover:text-white"><X size={12} /></button>
                  </div>
                  {searchResults.map((result) => (
                    <Link key={`${result.type}-${result.id}`} to={result.link} className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setShowSearchResults(false)}>
                      <span className="text-xl">{result.icon}</span>
                      <div className="flex-1">
                        <div className="font-mono text-sm text-white">{result.title}</div>
                        <div className="font-mono text-[9px] text-pink">{result.subtitle}</div>
                      </div>
                      <ChevronRight size={14} className="text-white/20" />
                    </Link>
                  ))}
                </div>
              )}
              
              {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 glass-dark rounded-xl border border-white/10 p-4 text-center">
                  <span className="font-mono text-xs text-white/30">Aucun résultat pour "{searchQuery}"</span>
                </div>
              )}
            </div>
            
            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="font-mono text-xs tracking-wider text-white/60 hover:text-mint transition">ACCUEIL</Link>
              <Link to="/browse" className="font-mono text-xs tracking-wider text-white/60 hover:text-mint transition">ARCHIVE</Link>
              <a href="#departments" className="font-mono text-xs tracking-wider text-white/60 hover:text-mint transition">DÉPARTEMENTS</a>
            </div>
            
            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="md:hidden text-white/60 hover:text-mint transition p-2"
            >
              <Menu size={24} />
            </button>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 py-3 glass-dark rounded-xl border border-white/10">
              <Link to="/" className="block px-4 py-2 font-mono text-xs tracking-wider text-white/60 hover:text-mint hover:bg-white/5 transition" onClick={() => setMobileMenuOpen(false)}>ACCUEIL</Link>
              <Link to="/browse" className="block px-4 py-2 font-mono text-xs tracking-wider text-white/60 hover:text-mint hover:bg-white/5 transition" onClick={() => setMobileMenuOpen(false)}>ARCHIVE</Link>
              <a href="#departments" className="block px-4 py-2 font-mono text-xs tracking-wider text-white/60 hover:text-mint hover:bg-white/5 transition" onClick={() => setMobileMenuOpen(false)}>DÉPARTEMENTS</a>
            </div>
          )}
          
          {/* Mobile Search Bar */}
          <div className="mt-3 md:hidden relative">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                className="font-body text-sm w-full pl-4 pr-10 py-2 glass-dark rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
            </div>
            
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 glass-dark rounded-xl border border-white/10 overflow-hidden z-50 max-h-80 overflow-y-auto">
                <div className="p-2 border-b border-white/10 flex justify-between items-center">
                  <span className="font-mono text-[9px] text-white/30 px-2">RÉSULTATS POUR "{searchQuery}"</span>
                  <button onClick={() => setShowSearchResults(false)} className="text-white/30 hover:text-white"><X size={12} /></button>
                </div>
                {searchResults.map((result) => (
                  <Link key={`${result.type}-${result.id}`} to={result.link} className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setShowSearchResults(false)}>
                    <span className="text-xl">{result.icon}</span>
                    <div className="flex-1">
                      <div className="font-mono text-sm text-white">{result.title}</div>
                      <div className="font-mono text-[9px] text-pink">{result.subtitle}</div>
                    </div>
                    <ChevronRight size={14} className="text-white/20" />
                  </Link>
                ))}
              </div>
            )}
            
            {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 glass-dark rounded-xl border border-white/10 p-4 text-center">
                <span className="font-mono text-xs text-white/30">Aucun résultat pour "{searchQuery}"</span>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - Mobile Responsive */}
      <section className="relative pt-28 sm:pt-36 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6 animate-fade-up text-center lg:text-left">
              <div className="inline-flex items-center gap-2 glass-dark rounded-full px-3 py-1.5 sm:px-4 sm:py-2 mx-auto lg:mx-0">
                <Archive size={10} className="text-mint sm:text-[12px]" />
                <span className="font-mono text-[8px] sm:text-[10px] tracking-wider text-mint">ARCHIVE OFFICIELLE ISIMM • 2026</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif font-light leading-[1.2] sm:leading-[1.15] text-white">
                La mémoire<span className="font-bold block gradient-text">de l'ISIMM</span>
              </h1>
              <p className="font-body text-white/40 text-base sm:text-lg leading-relaxed max-w-md mx-auto lg:mx-0">
                Accédez à l'intégralité des examens, cours, travaux pratiques et projets.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4 pt-2 justify-center lg:justify-start">
                <Link to="/browse" className="group bg-mint text-dark-bg px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-mono text-xs sm:text-sm font-semibold tracking-wider hover:bg-pink transition-all duration-200 flex items-center gap-2 shadow-lg shadow-mint/20">
                  EXPLORER L'ARCHIVE<ArrowRight size={12} className="group-hover:translate-x-1 transition sm:text-[14px]" />
                </Link>
                <a href="#departments" className="border border-white/20 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-mono text-xs sm:text-sm tracking-wider text-white/70 hover:border-mint hover:text-mint transition-all duration-200">
                  DÉPARTEMENTS
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {stats.map((stat) => (
                <div key={stat.id} className="bg-dark-surface/50 border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-5 backdrop-blur-sm hover:border-mint/30 transition-all duration-200 hover:-translate-y-1 animate-fade-up">
                  <div className={`text-${stat.color} mb-2 sm:mb-3`}>{stat.icon}</div>
                  <div className="font-serif text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="font-mono text-[8px] sm:text-[10px] tracking-wider text-white/40 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filter Buttons - Mobile Scrollable */}
      <section className="px-4 sm:px-6 py-4 sm:py-6 border-t border-dark-border/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-nowrap gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible">
            {resourceTypes.map((type) => (
              <button 
                key={type.id} 
                onClick={() => setActiveTypeFilter(type.id)} 
                className={`px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg font-mono text-[10px] sm:text-xs font-medium tracking-wider transition-all duration-200 flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTypeFilter === type.id ? 'bg-mint text-dark-bg shadow-md' : 'border border-white/15 text-white/50 hover:border-mint/50 hover:text-white'}`}
              >
                {type.icon}{type.name}<span className="ml-0.5 sm:ml-1 text-[8px] sm:text-[10px] opacity-70">({type.count})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Resources - Mobile Responsive */}
      <section className="px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div>
              <span className="font-mono text-[9px] sm:text-[10px] tracking-wider text-pink bg-pink/10 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">RESSOURCES RÉCENTES</span>
              <h2 className="text-xl sm:text-2xl font-serif font-light text-white mt-2">Derniers ajouts</h2>
              <p className="text-white/30 text-xs sm:text-sm mt-1">Les 3 derniers documents</p>
            </div>
            <Link to="/browse" className="font-mono text-[10px] sm:text-xs tracking-wider text-white/30 hover:text-mint transition flex items-center gap-1">
              VOIR TOUS DANS L'ARCHIVE <ChevronRight size={10} />
            </Link>
          </div>
          
          {loading ? (<div className="text-center py-12"><div className="text-white/40">Chargement...</div></div>) : recentResources.length === 0 ? (<div className="text-center py-12"><div className="text-white/40">Aucune ressource approuvée</div><div className="text-white/20 text-xs sm:text-sm mt-2">Ajoutez des ressources avec approvalStatus="approved"</div></div>) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentResources.map((resource) => (
                <Link key={resource.id} to={`/resource/${resource.documentId}`} className="group bg-dark-surface/50 border border-white/5 rounded-xl p-4 sm:p-5 hover:border-mint/30 hover:-translate-y-1 transition-all duration-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-mint/10 flex items-center justify-center group-hover:bg-mint/20 transition">
                      {getTypeIcon(resource.type)}
                    </div>
                    <span className={`font-mono text-[8px] sm:text-[9px] tracking-wider px-1.5 py-0.5 sm:px-2 sm:py-1 rounded border ${getTypeColor(resource.type)}`}>
                      {resource.type}
                    </span>
                  </div>
                  <h3 className="font-mono text-sm sm:text-base text-white group-hover:text-mint transition mb-2 line-clamp-2">{resource.title}</h3>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap">
                    <BookOpen size={10} className="text-mint sm:text-[12px]" />
                    <span className="font-mono text-[8px] sm:text-[10px] text-white/60">{resource.subject}</span>
                    <span className="text-white/20">•</span>
                    <GraduationCap size={10} className="text-pink sm:text-[12px]" />
                    <span className="font-mono text-[8px] sm:text-[10px] text-white/40">{resource.department} {resource.level}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 text-white/30 text-[10px] sm:text-xs">
                    <span className="flex items-center gap-1"><Calendar size={8} className="sm:text-[10px]" /> {resource.date}</span>
                    <span className="flex items-center gap-1"><Eye size={8} className="sm:text-[10px]" /> Vue</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Programs Section - Mobile Responsive */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-dark-border/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="font-mono text-[9px] sm:text-[10px] tracking-wider text-mint bg-mint/10 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">CYCLES</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-light text-white mt-2">Formations proposées</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {programList.map((program) => (
              program.hasData ? (
                <Link to={`/browse?program=${program.id}`} key={program.id} className="bg-dark-surface/50 border border-white/5 rounded-xl p-5 sm:p-6 transition-all duration-200 cursor-pointer hover:border-mint/30 hover:-translate-y-1" onMouseEnter={() => setHoveredCard(program.id)} onMouseLeave={() => setHoveredCard(null)}>
                  <div className={`mb-3 sm:mb-4 transition-all duration-200 ${hoveredCard === program.id ? 'scale-110' : ''}`}>
                    <div className={`text-${program.color}`}>{program.icon}</div>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl text-white mb-2">{program.name}</h3>
                  <p className="font-body text-white/40 text-xs sm:text-sm mb-4">{program.description}</p>
                  <div className="flex justify-between items-center">
                    <span className={`font-mono text-[10px] sm:text-xs text-${program.color}`}>{program.count} ressources</span>
                    <ChevronRight size={14} className="text-white/20 group-hover:text-mint transition-all sm:text-[16px]" />
                  </div>
                </Link>
              ) : (
                <div key={program.id} className="bg-dark-surface/50 border border-white/5 rounded-xl p-5 sm:p-6 opacity-60">
                  <div className="mb-3 sm:mb-4"><div className={`text-${program.color}`}>{program.icon}</div></div>
                  <h3 className="font-serif text-lg sm:text-xl text-white mb-2">{program.name}</h3>
                  <p className="font-body text-white/40 text-xs sm:text-sm mb-4">{program.description}</p>
                  <span className="inline-flex items-center gap-2 text-white/20 font-mono text-[10px] sm:text-xs"><Clock size={10} /> Prochainement</span>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Departments Section - Mobile Responsive */}
      <section id="departments" className="px-4 sm:px-6 py-16 sm:py-20 bg-gradient-to-b from-dark-surface/20 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="font-mono text-[9px] sm:text-[10px] tracking-wider text-orange bg-orange/10 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">DÉPARTEMENTS</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-light text-white mt-2">Explorez par filière</h2>
            <p className="text-white/30 text-xs sm:text-sm mt-2">{departments.length} départements</p>
          </div>
          
          {loading ? (<div className="text-center py-12"><div className="text-white/40">Chargement...</div></div>) : departments.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-white/40">Aucun département trouvé</div>
              <div className="text-white/20 text-xs sm:text-sm mt-2">Ajoutez des départements dans Strapi</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
              {departments.map((dept) => (
                <Link 
                  key={dept.id} 
                  to={`/department/${dept.name}`} 
                  className="group bg-dark-surface/50 border border-white/5 rounded-xl p-4 sm:p-5 text-center hover:border-mint/30 hover:-translate-y-1 transition-all duration-200" 
                  onMouseEnter={() => setHoveredCard(dept.name)} 
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className={`mb-2 sm:mb-3 transition-all duration-200 flex justify-center ${hoveredCard === dept.name ? 'scale-110' : ''}`}>
                    <div className={`text-${dept.color}`}>{dept.icon}</div>
                  </div>
                  <h3 className={`font-mono text-base sm:text-lg font-bold text-${dept.color}`}>{dept.name}</h3>
                  <p className="font-body text-white/40 text-[10px] sm:text-xs mt-1 line-clamp-1">{dept.fullName}</p>
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <span className="font-mono text-[8px] sm:text-[10px] text-white/40">{dept.count} ressource{dept.count !== 1 ? 's' : ''}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer - Mobile Responsive */}
      <footer className="border-t border-white/5 py-8 sm:py-10 px-4 sm:px-6 mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-2">
              <Archive size={16} className="text-mint sm:text-[18px]" />
              <span className="font-mono text-xs sm:text-sm tracking-tighter text-white">
                ISIMM<span className="text-pink">_Archive</span>
              </span>
            </div>
            <div className="font-mono text-[8px] sm:text-[9px] tracking-wider text-white/20">
              Institut Supérieur d'Informatique et de Mathématiques de Monastir<br />
              Préservation numérique • Accès libre • Archive officielle
              <div className="mt-2">
                <span className="text-white/20">Créé par </span>
                <a href="https://yahyasportfolio.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-mint hover:text-pink transition">Yahya Hcini</a>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="text-white/30 hover:text-mint transition text-[8px] sm:text-xs font-mono flex items-center gap-1 mx-auto md:mx-0"
                >
                  <AlertCircle size={10} /> Signaler un problème
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
      <PublicSubmit />

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        .animate-fade-up { animation: fadeUp 0.5s ease-out forwards; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .glass-dark { background: rgba(19, 19, 26, 0.7); backdrop-filter: blur(12px); border: 1px solid rgba(94, 224, 181, 0.15); }
        .gradient-primary { background: linear-gradient(135deg, #5EE0B5 0%, #FF6BC5 100%); }
        .gradient-text { background: linear-gradient(135deg, #5EE0B5 0%, #FF6BC5 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}

export default HomePage;